import { CommonGAssets } from '@hhgtech/hhg-components/gAssets'
import { createStyles, getStylesRef, keyframes, rem } from '@mantine/core'

export const arrowJump = keyframes({
  '0%': { transform: 'translateY(-4px)' },
  '50%': { transform: 'translateY(4px)' },
  '100%': { transform: 'translateY(-4px)' },
})

export const fadeOut = keyframes({
  '0%': { transform: 'scale(1)', opacity: 1 },
  '50%': {
    transform: 'scale(1.2)',
    opacity: 0,
  },
  '100%': { transform: 'scale(1.2)', opacity: 0 },
})

export const rotate = keyframes({
  '0%': { transform: 'rotate(0)' },
  '100%': { transform: 'rotate(360deg)' },
})

export const fadeIn = keyframes({
  '0%': { transform: 'scale(1.2)', opacity: 0 },
  '50%': { transform: 'scale(1)', opacity: 1 },
  '100%': { transform: 'scale(1)', opacity: 1 },
})

export default createStyles((theme) => {
  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: 40,
      [theme.fn.largerThan('sm')]: {
        gap: 80,
      },
    },
    banner: {
      position: 'relative',
      backgroundImage: `url(${CommonGAssets.getAssetPath(
        'appIntro/hero-bg-mobile.jpg',
      )})`,
      backgroundSize: 'cover',
      padding: '32px 0 16px',
      [theme.fn.largerThan('sm')]: {
        backgroundImage: `url(${CommonGAssets.getAssetPath(
          'appIntro/hero-bg.jpg',
        )})`,
        padding: '16px 0 36px',
      },
    },
    inner: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    },
    body: {
      [theme.fn.largerThan('sm')]: {
        position: 'relative',
        '&:before': {
          content: '""',
          display: 'block',
          paddingBottom: 'calc(50% - 80px)',
          height: 0,
        },
      },
    },
    bodyInner: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      [theme.fn.smallerThan('sm')]: {
        flexDirection: 'column',
        gap: 16,
      },
      [theme.fn.largerThan('sm')]: {
        gap: 24,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      },
    },
    heading: {
      fontSize: 72,
      fontWeight: 500,
      lineHeight: rem(86),
      [theme.fn.smallerThan('sm')]: {
        fontSize: 32,
        lineHeight: rem(42),
        textAlign: 'center',
      },
    },
    thumbnail: {
      maxHeight: 640,
      [theme.fn.smallerThan('sm')]: {
        maxHeight: 340,
      },
    },
    info: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 16,
      [theme.fn.largerThan('sm')]: {
        alignItems: 'flex-start',
        width: '55%',
        gap: 24,
      },
    },
    logoNav: {
      [theme.fn.smallerThan('sm')]: {
        display: 'none',
      },
    },
    logo: {
      width: 40,
    },
    logoHead: {
      fontSize: 14,
      fontWeight: 600,
    },
    nav: {
      [theme.fn.smallerThan('sm')]: {
        display: 'none',
      },
    },
    heroDesc: {
      fontSize: 22,
      lineHeight: rem(34),
      [theme.fn.largerThan('sm')]: {
        fontSize: 32,
        lineHeight: rem(42),
      },
    },
    bannerImageFrame: {
      [theme.fn.largerThan('sm')]: {
        width: '45%',
        flexGrow: 1,
      },
    },
    bannerImageFrameInner: {
      [theme.fn.largerThan('sm')]: {
        position: 'relative',
        '&:before': {
          content: '""',
          height: 0,
          paddingBottom: '100%',
          display: 'block',
        },
      },
    },
    bannerImageFrameInnerInner: {
      [theme.fn.largerThan('sm')]: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        img: {
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
        },
      },
    },
    scrollToNext: {
      position: 'absolute',
      bottom: 32,
      left: 0,
      right: 0,
      zIndex: 1,
      [theme.fn.largerThan('sm')]: {
        bottom: 24,
      },
    },
    scrollToNextBtn: {
      cursor: 'pointer',
      width: 88,
      color: theme.fn.primaryColor(),
      borderRadius: '100%',
      backgroundColor: 'transparent',
      transition: 'all 0.3s ease',
      [theme.fn.smallerThan('sm')]: {
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        backdropFilter: 'blur(8px)',
      },
      [theme.fn.largerThan('sm')]: {
        width: 112,
      },
    },
    scrollToNextFrame: {
      [theme.fn.smallerThan('sm')]: {
        borderRadius: '100%',
        border: '1px solid #fff',
        top: '20px !important' as any,
        left: '20px !important' as any,
        right: '20px !important' as any,
        bottom: '20px !important' as any,
        width: 'auto !important' as any,
        height: 'auto !important' as any,
      },
    },
    scrollToNextThumb: {
      [theme.fn.largerThan('sm')]: {
        width: 100,
      },
      '&:hover': {
        [`.${getStylesRef('scrollToNextLogo')}`]: {
          animationPlayState: 'paused',
        },
        [`.${getStylesRef('scrollToNextIcon')}`]: {
          animation: `${arrowJump} 0.8s infinite`,
        },
      },
    },
    scrollToNextLogo: {
      ref: getStylesRef('scrollToNextLogo'),
      animation: `${rotate} 8s linear infinite`,
      [theme.fn.smallerThan('sm')]: {
        top: '4px !important' as any,
        left: '4px !important' as any,
        width: 'calc(100% - 8px)!important' as any,
        height: 'calc(100% - 8px)!important' as any,
      },
    },
    scrollToNextIcon: {
      ref: getStylesRef('scrollToNextIcon'),
    },
  }
})
